import React, { useEffect, useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import LoadingOverlay from '../../loadingOverlay/LoadingOverlay';
import ManualList from '../ManualList';
import useApi from '../../hooks/useApi';
import FilteredGroupsSelector from '../../contacts/FilteredGroupsSelector';
import FilteredSegmentSelector from '../../contacts/FilteredSegmentSelector';
import ShoppingList from '../../editor/ShoppingList';
import AllowedRecipientsList from '../../contacts/AllowedRecipientsList';
import Divider from '@mui/material/Divider';
import GroupIcon from '@mui/icons-material/Group';
import ListAltIcon from '@mui/icons-material/ListAlt';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { Alert } from '@mui/material';
import JoinInnerIcon from '@mui/icons-material/JoinInner';
import GroupsIcon from '@mui/icons-material/Groups';
import buildUrl from '../../buildUrl';
import useSuspenseFetch from '../../hooks/useSuspenseFetch';

const emailAddressesLimit = 20;

const Audience = ({ audience, onChangeAudience, onNext, onBack, drawerWidth, steps, step, campaign, isProofSent, audienceName, titleBarDepth, setStep }) => {
    const { data: account } = useSuspenseFetch('account');
    const [isInitialising, setisInitialising] = useState(true);
    const [segments, setSegments] = useState([]);
    const [isFormValid, setIsFormValid] = useState(false);
    const { handleGet } = useApi();
    const { allowedRecipients } = account;
    const isInternalComms = account?.licence?.isInternalComms ?? false;
    const [allContactsCount, setAllContactsCount] = useState(0);
    const [numberOfSubscribers, setNumberOfSubscribers] = useState(0);

    const audienceTabs = [
        { label: 'All Contacts', value: 4, icon: <GroupsIcon fontSize="large" /> },
        { label: 'Groups', value: 2, icon: <GroupIcon fontSize="large" /> },
        { label: 'Segment', value: 3, icon: <JoinInnerIcon fontSize="large" /> },
        { label: 'Manual Entry', value: 0, icon: <ListAltIcon fontSize="large" /> }
    ];

    const handleFetchSegments = async () => {
        const response = await handleGet('segments');

        if (!response.ok) {
            console.error(response?.error);
            return;
        }

        const data = await response.json();
        setSegments(data);
    };

    const handleClickSegment = id => {
        if (audience.selectedSegment === id) {
            onChangeAudience({ selectedSegment: null });
        }
        else {
            onChangeAudience({ selectedSegment: id, groupIds: [], emailAddresses: [] });
        }
    };

    const handleToggleSelectedGroups = groupIds => {
        onChangeAudience({ groupIds, selectedSegment: null, emailAddresses: [] });
    };

    const handleChangeAudience = audienceType => {
        onChangeAudience({
            audienceType,
            ...(audience.audienceType === 0 && { selectedSegment: null, groupIds: [] }),
            ...(audience.audienceType === 2 && { selectedSegment: null, emailAddresses: [] }),
            ...(audience.audienceType === 4 && { selectedSegment: null, groupIds: [], emailAddresses: [] })
        });
    };

    const handleNext = () => {
        onNext({
            audience: audience.audienceType,
            ...(audience.audienceType === 0 && { emailAddresses: audience.emailAddresses, groupIds: [], segmentId: null }),
            ...(audience.audienceType === 2 && { groupIds: audience.groupIds, emailAddresses: [], segmentId: null }),
            ...((audience.audienceType === 3 && audience.selectedSegment) && { segmentId: audience.selectedSegment, emailAddresses: [], groupIds: [] }),
            ...(audience.audienceType === 4 && { segmentId: null, emailAddresses: [], groupIds: [] }),
            sendToAll: audience.audienceType === 4
        });
    };

    const handleCheckIsValid = () => {
        if (audience.audienceType === null) return false;

        return (
            (audience.audienceType === 0 && audience.emailAddresses.length > 0 && audience.emailAddresses.length <= emailAddressesLimit) ||
            (audience.audienceType === 2 && audience.groupIds && audience.groupIds.length > 0) ||
            (audience.audienceType === 3 && audience.selectedSegment) ||
            audience.audienceType === 4
        );
    };

    const handleFetchContactsCount = async () => {
        const url = buildUrl('contacts/count', {
            sendDateTime: new Date(campaign.sendDateTime ?? Date.now()).toISOString(),
            includeSuppressed: false
        });
        const response = await handleGet(url);

        if (!response.ok) {
            console.error(response?.error);
            return null;
        }

        const count = await response.json();

        setAllContactsCount(count);
    };

    const handleFetchNumberOfSubscribers = async () => {
        const url = buildUrl('campaigns/audience-count', {
            sendDateTime: new Date(campaign.sendDateTime ?? Date.now()).toISOString(),
            ...(audience.groupIds.length > 0 && { groupIds: audience.groupIds }),
            ...(audience.selectedSegment && { segmentId: audience.selectedSegment })
        });

        const response = await handleGet(url);

        if (!response.ok) {
            console.error(response?.error);
            return;
        }

        const data = await response.json();

        data && setNumberOfSubscribers(data);
    };

    const handleInit = async () => {
        await handleFetchSegments();
        await handleFetchContactsCount();
        setisInitialising(false);
    };

    useEffect(() => {
        handleInit();
        setIsFormValid(handleCheckIsValid());
    }, []);

    useEffect(() => {
        setIsFormValid(handleCheckIsValid());
    }, [audience]);

    useEffect(() => {
        const hasValidGroups = audience.audienceType === 2 && audience.groupIds.length > 0;
        const hasValidSegment = audience.audienceType === 3 && audience.selectedSegment !== null;

        if (hasValidGroups || hasValidSegment) {
            handleFetchNumberOfSubscribers();
        }
        else {
            setNumberOfSubscribers(0);
        }
    }, [audience.audienceType, audience.groupIds, audience.selectedSegment]);

    return (
        <>
            <AppBar position="sticky" color="inherit" sx={{ top: 64, zIndex: titleBarDepth }}>
                <Toolbar>
                    <Typography component="h1" variant="h6">Audience</Typography>
                </Toolbar>
            </AppBar>
            <div style={{ marginRight: drawerWidth, flexGrow: 1, height: 'calc(100vh - 130px)', overflowY: 'auto' }}>
                {isInitialising ? <LoadingOverlay /> : (
                    <Container disableGutters>
                        <Paper sx={{ m: 2, p: 3 }}>
                            <Tabs
                                value={audience.audienceType}
                                onChange={(event, newValue) => handleChangeAudience(newValue)}
                                variant="fullWidth"
                                centered
                            >
                                {audienceTabs.map(({ value, label, icon }) => (
                                    <Tab
                                        key={value}
                                        icon={icon}
                                        label={(
                                            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                                                {label}
                                            </Typography>
                                        )}
                                        value={value}
                                        wrapped
                                    />
                                ))}
                            </Tabs>
                            <Divider sx={{ mb: 3 }} />
                            {audience.audienceType === null && (
                                <Alert severity="info">Please choose an Audience Type.</Alert>
                            )}
                            <div hidden={audience.audienceType !== 0}>
                                <Alert severity="info">If any of these email addresses are not currently in your account, they will be automatically added. After you have sent your campaign they will appear as ungrouped Contacts.</Alert>
                                <FormControl fullWidth>
                                    <ManualList
                                        emailAddresses={audience.emailAddresses}
                                        setEmailAddresses={emailAddresses => onChangeAudience({ ...audience, emailAddresses })}
                                        emailAddressesLimit={20}
                                    />
                                    <FormHelperText margin="dense">Send up to 20 addresses.</FormHelperText>
                                </FormControl>
                                {(isInternalComms && allowedRecipients.length > 0) && (
                                    <Box sx={{ marginTop: 2 }}>
                                        <AllowedRecipientsList allowedRecipients={allowedRecipients} />
                                    </Box>
                                )}
                            </div>
                            <div hidden={audience.audienceType !== 2}>
                                {audience.audienceType === 2 && (
                                    <FilteredGroupsSelector
                                        selectedGroups={audience.groupIds}
                                        setSelectedGroups={groupIds => handleToggleSelectedGroups(groupIds)}
                                        limitedHeight={false}
                                        newGroup={false}
                                        numberOfSubscribers={numberOfSubscribers}
                                    />
                                )}
                            </div>
                            <div hidden={audience.audienceType !== 3}>
                                {(segments && segments.length > 0) ? (
                                    <FilteredSegmentSelector
                                        selectedSegment={audience.selectedSegment}
                                        setSelectedSegment={id => handleClickSegment(id)}
                                        limitedHeight={false}
                                    />
                                ) : (
                                    'No segments found'
                                )}
                            </div>
                            <div hidden={audience.audienceType !== 4}>
                                <Alert severity="info">
                                    This option sends to <strong>All Contacts</strong> in your account. <br />
                                    Total Contacts - <strong>{allContactsCount}</strong> (Suppressed not included).
                                </Alert>
                            </div>
                        </Paper>
                    </Container>
                )}
            </div>
            <ShoppingList
                drawerWidth={drawerWidth}
                stepNumber={step}
                steps={steps}
                onNext={handleNext}
                onBack={onBack}
                disabled={!isFormValid}
                campaign={campaign}
                isProofSent={isProofSent}
                audienceName={audienceName}
                setStep={setStep}
            />
        </>
    );
};

export default Audience;